import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { PaymentService, CreditCardService, ErrorHandlerService, ValidDatePipe, ViewportService } from 'epay-library';
import { IPaymentAccount } from 'epay-library/lib/features/common/models/payment/paymentaccount';
import { IEMPPaymentList } from 'epay-library/lib/features/employer/models/payment/achd/employerPaymentList';
import { IEMPCCPayment } from 'epay-library/lib/features/employer/models/payment/creditcard/employerCCPayment';
import { IEMPPaymentCollection } from 'epay-library/lib/features/employer/models/payment/employerPaymentCollection';
import * as XLSX from 'xlsx';

const PayorValidationTypes = {
  scheduldedPayment: 'scheduledPayment',
  creditCard: 'creditCard',
  eftEdiFile: 'eftEdiFile',
};

type AOA = any[][];

@Component({
  selector: 'emp-cc-validation',
  templateUrl: './empccvalidation.component.html'
})
export class EMPCCValidationComponent implements OnInit, OnChanges {
  @Input() baseurl = '';
  @Input() secondaryIdAlias = '';
  @Input() isEditPayment: boolean = false;
  @Input() validationType = '';
  @Input() editPayorCollection: IEMPPaymentCollection[] = [];
  @Input() selectedPaymentAccount: IPaymentAccount = {} as IPaymentAccount;
  @Output() validPayorEmitter_CO = new EventEmitter<{
    payorCollection: IEMPPaymentCollection[];
  }>();
  @Output() validPayorCreditCardEmitter_CO = new EventEmitter<{
    creditCardPayment: IEMPCCPayment;
  }>();

  @ViewChild('LastName', { static: false }) nameField!: ElementRef;
  @ViewChild('inputFile', { static: false }) inputFile!: ElementRef;

  //Configuration Settings for SDU Specific
  validateCaseNumber: boolean = true;

  //Form Group for payor collection
  payorFrmGp: FormGroup = new FormGroup({});

  //Payor Collection arrays
  previousPaymentPayors: IEMPPaymentCollection[] = [];
  previousAllPayors: IEMPPaymentCollection[] = [];
  payorCollection: IEMPPaymentCollection[] = [];

  //Scheduled Payments
  scheduledpayments: IEMPPaymentList[] = [];

  //Credit Card Payment
  empCCPayment: IEMPCCPayment = {} as IEMPCCPayment;

  //File import
  data: any;
  wopts: XLSX.WritingOptions = { bookType: 'xlsx', type: 'array' };

  //Edit payor collection
  isEditPayor = false;
  editPayorIndex = 0;

  //Input pattern validations
  currencyPattern = /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/;
  ssnPattern = /^(?!000|666)[0-9]{3}([ -]?)(?!00)[0-9]{2}\1(?!0000)[0-9]{4}$/;

  payorChkType: any;

  fileImported = false;
  isFileValid = false;

  errorMessage = '';
  validationErrorMessage = '';
  toggleEditPayor = false;

  noPreviousPayors = '';
  isLoadingPreviousPayors = false;

  showPayorCollectionForm = false;
  numOfPayments = 0;
  totalAmtOfPayments = 0;
  payorsValid = false;
  numInvalid = 0;
  numWarning = 0;
  isValdiatingPayment = false;

  ssnHide = true;

  animateInput = false;
  atLeastOnePayorSelected = false;
  validateAttemptMade = false;
  insertUpdateError = false;
  nameChangeOccured = false;
  insertUpdateErrorMessage = '';

  fileImportHint = `File must contain 4 columns with headers titled "Last Name", "SSN", "FSR Number", and "Amount"`;
  fileErrorMessage = '';
  payorDetailError = '';

  previousPayorsChecked = false;
  allPayorsChecked = false;
  allChecked: boolean = false;

  isCreditCardValidation: boolean = false;
  isEftFileValidation: boolean = false;
  isScheduledPaymentValidation: boolean = false;

  constructor(
    private formbuilder: FormBuilder,
    private paymentService: PaymentService,
    private creditCardService: CreditCardService,
    private errorHandlerService: ErrorHandlerService,
    private router: Router,
    public dialog: MatDialog,
    private datePipe: DatePipe,
    private validDatePipe: ValidDatePipe,
    public snackbar: MatSnackBar,
    private viewpointService: ViewportService,
    public tooltip: MatTooltipModule
  ) {
    this.previousAllPayors = [];
    this.previousPaymentPayors = [];
    this.payorCollection = [];
  }

  ngOnInit(): void {
    this.setValidationType();

    if (this.secondaryIdAlias === '') {
      this.secondaryIdAlias = 'FSR Number';
    }

    this.payorFrmGp = this.formbuilder.group({
      filename: ' Choose File',
      collectionFrmGp: this.formbuilder.group({
        lastname: ['', Validators.minLength(1)],
        ssn: [''],
        casenumber: ['', Validators.minLength(1)],
        amount: ['', Validators.pattern(this.currencyPattern)],
      }),
      isValid: ['false', Validators.requiredTrue],
    });
  }

  ngOnChanges(): void {
    if (this.isEditPayment) {
      this.payorCollection = this.editPayorCollection;
    }
  }

  emitValidPayorPerValidation() {
    if (this.isScheduledPaymentValidation) {
      this.validPayorEmit();
    } else if (this.isCreditCardValidation) {
      this.validCreditCardEmit();
    }
  }

  validPayorEmit() {
    if (this.payorsValid) {
      this.validPayorEmitter_CO.emit({
        payorCollection: this.payorCollection,
      });
    }
  }

  validCreditCardEmit() {
    this.empCCPayment = {
      batchType: 'WEB',
      payerType: 'EMP',
      confirm: false,
      fee: 0,
      feePercentage: 0,
      amount: this.totalAmtOfPayments,
      totalAmount: this.totalAmtOfPayments,
      participantCollection: this.payorCollection,
      firstName: '',
      lastName: '',
      ssn: '',
      addressLine1: '',
      addressLine2: '',
      addressCity: '',
      addressState: '',
      addressCountry: '',
      addressZIP5: '',
      email: '',
      phone: '',
    };

    this.creditCardService
      .insertEmployerCreditCardAttempt(this.baseurl, this.empCCPayment)
      .subscribe({
        next: (response: any) => {
          this.empCCPayment.fee = response.value.convenienceAmount;
          this.empCCPayment.feePercentage =
            response.value.convenienceFeePercentage;
          this.empCCPayment.totalAmount = response.value.totalPaymentAmount;
          this.empCCPayment.participantCollection = this.payorCollection;
        },
        error: (error: any) => {
          this.logError(error);
          this.handleError(error.status);
        },
        complete: () => {
          this.validPayorCreditCardEmitter_CO.emit({
            creditCardPayment: this.empCCPayment,
          });
        },
      });
  }

  validatePayors() {
    this.numInvalid = 0;
    this.numWarning = 0;
    this.noPreviousPayors = '';
    this.validationErrorMessage = '';
    this.isValdiatingPayment = true;
    this.validateAttemptMade = true;
    this.nameChangeOccured = false;

    const allowZeroAmount = false;

    let foundError = false;

    if (this.atLeastOnePayorSelected) {
      this.payorCollection = this.payorCollection.filter((x) => x.isSelected);
      this.payorCollection.forEach((payor) => {
        payor.hasError = false;
        payor.isValidated = false;
        payor.errorMessage = '';
        // Check for invalid payors
        if (payor.amount === null || payor.amount === undefined) {
          foundError = true;
          payor.hasError = true;
          payor.isValidated = true;
          payor.errorMessage = 'Missing Amount';
        } else if (
          Number.isNaN(payor.amount) ||
          payor.amount === 0 ||
          payor.amount < 1.0 ||
          payor.amount > 9999.99
        ) {
          foundError = true;
          payor.hasError = true;
          payor.isValidated = true;
          payor.errorMessage = 'Invalid Amount';
        } else if (
          this.validateCaseNumber &&
          (payor.caseNumber === null || payor.caseNumber === '')
        ) {
          foundError = true;
          payor.hasError = true;
          payor.isValidated = true;
          payor.errorMessage = 'Missing ' + this.secondaryIdAlias;
        } else {
          payor.hasError = false;
          payor.isValidated = false;
          payor.errorMessage = '';
        }
        payor.ssn = payor.ssn.toString().replace(/-/g, '');
        payor.ssn = payor.ssn.toString().replace(/\s/g, '');
        payor.hadEdit = false;
      });

      if (foundError) {
        let foundInvalidCount = 0;
        this.payorsValid = false;
        this.isValdiatingPayment = false;
        this.payorFrmGp.patchValue({
          isValid: this.payorsValid,
        });
        this.payorCollection.forEach((payor) => {
          if (payor.hasError) {
            foundInvalidCount++;
          }
        });
        this.numInvalid = foundInvalidCount;
      }

      if (
        this.payorCollection.length > 0 &&
        this.atLeastOnePayorSelected &&
        !foundError
      ) {
        this.payorCollection.forEach((payor) => {
          //Update ssn to all 0s if blank
          if (payor.ssn.length === 0) {
            payor.ssn = '000000000';
          }
          //payor.caseNumber = payor.caseNumber?.toString().padStart(9, '0');
        });

        this.paymentService
          .isValidPayment(
            this.baseurl,
            this.payorCollection,
            allowZeroAmount.toString(),
            this.validateCaseNumber.toString()
          )
          .subscribe({
            next: (valid: any) => {
              this.payorCollection.forEach((element) => {
                element.hasError = false;
                element.hasWarning = false;
                element.isValidated = true;
              });
              this.payorsValid = true;
              this.nameChangeOccured = false;
              this.payorFrmGp.patchValue({
                isValid: this.payorsValid,
              });
              this.isValdiatingPayment = false;
            },
            error: (error: any) => {
              this.payorsValid = false;
              this.isValdiatingPayment = false;
              this.payorFrmGp.patchValue({
                isValid: this.payorsValid,
              });
              if (error.status === 400) {
                // First, reset all payments hasError to false
                this.payorCollection.forEach((element) => {
                  element.isValidated = true;
                  element.hasError = false;
                  element.hasWarning = false;
                  element.errorMessage = '';
                });
                // Second, add hasError to those indexes returned
                error.error.invalidParticipantPositions.forEach(
                  (invalidPayor: any) => {
                    if (
                      invalidPayor.correctedName !== undefined &&
                      invalidPayor.correctedName !== null &&
                      invalidPayor.correctedName !== ''
                    ) {
                      this.nameChangeOccured = true;
                      this.payorCollection[invalidPayor.index].hasWarning =
                        true;
                      this.payorCollection[invalidPayor.index].lastName =
                        invalidPayor.correctedName;
                      this.payorCollection[invalidPayor.index].errorMessage =
                        invalidPayor.error;
                      this.numWarning++;
                    } else {
                      this.payorCollection[invalidPayor.index].hasError = true;
                      this.payorCollection[invalidPayor.index].errorMessage =
                        invalidPayor.error;
                      this.numInvalid++;
                    }
                  }
                );
                this.validationErrorMessage = error.error.failureMessage;
                this.validationErrorMessage =
                  this.validationErrorMessage.replace(/^"|"$/g, '');
              } else {
                this.logError(error);
                this.handleError(error.status);
              }
            },
            complete: () => {
              if (this.payorsValid) {
                this.emitValidPayorPerValidation();
              }
            },
          });
      }
    } else {
      this.isValdiatingPayment = false;
    }
  }

  getPreviousPaymentPayors(event: any) {
    this.toggleEditPayor = false;
    this.payorsValid = false;
    this.numInvalid = 0;
    this.numWarning = 0;
    this.isFileValid = false;
    this.fileImported = false;
    this.noPreviousPayors = '';
    this.nameChangeOccured = false;
    this.isLoadingPreviousPayors = false;
    this.previousPaymentPayors = [];
    this.previousAllPayors = [];
    this.payorCollection = [];

    let lastPaymentIndex = 0;
    this.payorFrmGp.patchValue({
      filename: '',
      isValid: this.payorsValid,
    });
    this.payorFrmGp.updateValueAndValidity();

    if (!event.target.checked) {
      this.previousPayorsChecked = false;
    } else {
      this.previousPayorsChecked = true;
      this.allPayorsChecked = false;
      this.isLoadingPreviousPayors = true;

      this.paymentService
        .getPayments(
          this.baseurl,
          this.selectedPaymentAccount.scheduledPaymentAccountKey,
          true
        )
        .subscribe({
          next: (payments: any) => {
            this.scheduledpayments = payments;
            //Filter cancelled payments that don't have payor listed anymore
            this.scheduledpayments.filter(
              (p) => p.participantCollection.length > 0
            );

            //If we have scheduled payment left after filter, sort them and add to payment Collection for view
            if (this.scheduledpayments.length > 0) {
              this.scheduledpayments.sort(
                (a, b) =>
                  <any>new Date(a.scheduleStartDate) -
                  <any>new Date(b.scheduleStartDate)
              );

              lastPaymentIndex = this.scheduledpayments.length - 1;

              this.previousPaymentPayors =
                this.scheduledpayments[lastPaymentIndex].participantCollection;
              this.previousPaymentPayors.forEach((payor) => {
                payor.isSelected = false;
                payor.isValidated = false;
                payor.hasError = false;
                payor.caseNumber = payor.secondaryId;
              });
              this.payorCollection = this.payorCollection.concat(
                this.previousPaymentPayors
              );
              this.isLoadingPreviousPayors = false;
              this.allChecked = false;
            } else {
              this.noPreviousPayors =
                'No previous payors found for the selected bank account.';
              this.isLoadingPreviousPayors = false;
            }
          },
          error: (error: any) => {
            this.isLoadingPreviousPayors = false;
            if (error.status !== 404) {
              this.logError(error);
              this.handleError(error.status);
            } else {
              this.noPreviousPayors =
                'No previous payors found for the selected bank account.';
              this.isLoadingPreviousPayors = false;
            }
          },
        });
    }
  }

  getAllPreviousPayors(event: any) {
    this.toggleEditPayor = false;
    this.payorsValid = false;
    this.numInvalid = 0;
    this.numWarning = 0;
    this.isFileValid = false;
    this.fileImported = false;
    this.noPreviousPayors = '';
    this.nameChangeOccured = false;
    this.isLoadingPreviousPayors = false;
    this.previousPaymentPayors = [];
    this.previousAllPayors = [];
    this.payorCollection = [];

    this.payorFrmGp.patchValue({
      filename: '',
      isValid: this.payorsValid,
    });
    this.payorFrmGp.updateValueAndValidity();

    if (!event.target.checked) {
      this.allPayorsChecked = false;
    } else {
      this.allPayorsChecked = true;
      this.previousPayorsChecked = false;

      this.isLoadingPreviousPayors = true;

      this.paymentService
        .getPayments(
          this.baseurl,
          this.selectedPaymentAccount.scheduledPaymentAccountKey,
          true
        )
        .subscribe({
          next: (payments: any) => {
            this.scheduledpayments = payments;
            //Filter cancelled payments that don't have payor listed anymore
            this.scheduledpayments.filter(
              (p) => p.participantCollection.length > 0
            );

            //If we have scheduled payment left after filter, sort them and add to payment Collection for view
            if (this.scheduledpayments.length > 0) {
              this.scheduledpayments.sort(
                (a, b) =>
                  <any>new Date(a.scheduleStartDate) -
                  <any>new Date(b.scheduleStartDate)
              );

              //Get all previous participants
              this.scheduledpayments.forEach((payment) => {
                this.previousAllPayors = this.previousAllPayors.concat(
                  payment.participantCollection
                );
              });
              //Set defaults for all previous participants
              this.previousAllPayors.forEach((prevPayor) => {
                prevPayor.isSelected = false;
                prevPayor.hasError = false;
                prevPayor.isValidated = false;
                prevPayor.caseNumber = prevPayor.secondaryId;
              });
              //Remove duplicates with same name, ssn, and FSR Number
              this.previousAllPayors = this.previousAllPayors.filter(
                (v, i, a) =>
                  a.findIndex(
                    (t) =>
                      t.lastName.toUpperCase() === v.lastName.toUpperCase() &&
                      t.ssn === v.ssn &&
                      t.caseNumber === v.caseNumber
                  ) === i
              );

              //Set previous to collection for UI display
              this.payorCollection = this.previousAllPayors;

              //Set loading values for display
              this.isLoadingPreviousPayors = false;
              this.allChecked = false;
            }
          },
          error: (error: any) => {
            this.isLoadingPreviousPayors = false;
            if (error.status !== 404) {
              this.logError(error);
              this.handleError(error.status);
            } else {
              this.noPreviousPayors =
                'No previous payors found for the selected bank account.';
              this.isLoadingPreviousPayors = false;
            }
          },
        });
    }
  }

  toggleSSNHide() {
    this.ssnHide = !this.ssnHide;
  }

  onFileChange(evt: any) {
    this.previousPayorsChecked = false;
    this.allPayorsChecked = false;
    this.payorsValid = false;
    this.numInvalid = 0;
    this.numWarning = 0;
    this.isFileValid = false;
    this.noPreviousPayors = '';
    this.nameChangeOccured = false;

    let headerRowIndex = -1;
    let lastNameHeaderIndex = -1;
    let ssnHeaderIndex = -1;
    let caseHeaderIndex = -1;
    let amountHeaderIndex = -1;
    this.payorCollection = [];
    this.payorFrmGp.patchValue({
      filename: '',
    });

    /* wire up file reader */
    let target: DataTransfer = evt.target as DataTransfer;
    if (target.files.length > 1) {
      this.isFileValid = false;
      this.fileErrorMessage = 'Cannot import multiple files at once.';
      target = new DataTransfer();
      throw new Error('Cannot use multiple files');
    }
    if (
      !target.files[0].name.endsWith('xlsx') &&
      !target.files[0].name.endsWith('xls')
    ) {
      target = new DataTransfer();
      this.isFileValid = false;
      this.fileErrorMessage = 'File type must be Excel format (.xlsx or .xls)';
      throw new Error('File type not accepted');
    }
    // this.payorCollection = []; Addd import to existing payors
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const worksheetName: string = wb.SheetNames[0];
      const worksheet: XLSX.WorkSheet = wb.Sheets[worksheetName];

      /* find the headers */
      this.data = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as AOA;
      for (let x = 0; x < this.data.length; x++) {
        if (this.data[x][0] !== undefined) {
          if (
            this.data[x][0].toString().trim().toLowerCase() ===
              'employee last name' ||
            this.data[x][0].toString().trim().toLowerCase() === 'lastname' ||
            this.data[x][0].toString().trim().toLowerCase() === 'last name' ||
            this.data[x][0].toString().trim().toLowerCase() ===
              'social security number' ||
            this.data[x][0].toString().trim().toLowerCase() === 'ssn' ||
            this.data[x][0].toString().trim().toLowerCase() === 'amt' ||
            this.data[x][0].toString().trim().toLowerCase() === 'amount' ||
            this.data[x][0].toString().trim().toLowerCase() === 'payment amount'
          ) {
            headerRowIndex = x;
            break;
          }
        }
      }

      if (headerRowIndex === -1) {
        this.payorFrmGp.patchValue({
          filename: '',
        });
        this.isFileValid = false;
        target = new DataTransfer();
        if (this.validateCaseNumber) {
          this.fileErrorMessage =
            `Invalid File Format. File must be Excel document and contain  at least 4 columns with headers titled
          "Employee Last Name", "SSN", ` +
            this.secondaryIdAlias +
            ` and "Amount".  Please refer to Sample File Example.`;
        } else {
          this.fileErrorMessage = `Invalid File Format. File must be Excel document and contain  at least 3 columns with headers titled
                                "Employee Last Name", "SSN", and "Amount".  Please refer to Sample File Example.`;
        }
        return;
      }

      /* Find the index for data */
      const headercheck = this.data[headerRowIndex];
      for (let x = 0; x < headercheck.length; x++) {
        if (
          headercheck[x].toString().toLowerCase() === 'employee last name' ||
          headercheck[x].toString().toLowerCase() === 'lastname' ||
          headercheck[x].toString().toLowerCase() === 'last name'
        ) {
          lastNameHeaderIndex = x;
        }
        if (
          headercheck[x].toString().toLowerCase() === 'ssn' ||
          headercheck[x].toString().toLowerCase() === 'social security number'
        ) {
          ssnHeaderIndex = x;
        }

        if (
          headercheck[x].toString().toLowerCase() === 'fsr number' ||
          headercheck[x].toString().toLowerCase() === 'case' ||
          headercheck[x].toString().toLowerCase() === 'case id' ||
          headercheck[x].toString().toLowerCase() === 'fsr' ||
          headercheck[x].toString().toLowerCase() === 'fsr #' ||
          headercheck[x].toString().toLowerCase() === 'fsr number'
        ) {
          caseHeaderIndex = x;
        }

        if (
          headercheck[x].toString().toLowerCase() === 'payment amount' ||
          headercheck[x].toString().toLowerCase() === 'amount' ||
          headercheck[x].toString().toLowerCase() === 'amt'
        ) {
          amountHeaderIndex = x;
        }
      }

      if (this.validateCaseNumber) {
        if (
          lastNameHeaderIndex === -1 ||
          ssnHeaderIndex === -1 ||
          amountHeaderIndex === -1 ||
          caseHeaderIndex === -1
        ) {
          this.payorFrmGp.patchValue({
            filename: '',
          });
          this.isFileValid = false;
          target = new DataTransfer();
          this.fileErrorMessage =
            `Invalid File Format. File must be Excel document and contain at least 3 columns with headers titled
                                  "Employee Last Name", "SSN", ` +
            this.secondaryIdAlias +
            ` and "Amount".  Please refer to Sample File.`;
          return;
        }
      } else {
        if (
          lastNameHeaderIndex === -1 ||
          ssnHeaderIndex === -1 ||
          amountHeaderIndex === -1
        ) {
          this.payorFrmGp.patchValue({
            filename: '',
          });
          this.isFileValid = false;
          target = new DataTransfer();
          this.fileErrorMessage = `Invalid File Format. File must be Excel document and contain at least 3 columns with headers titled
                                  "Employee Last Name", "SSN", and "Amount".  Please refer to Sample File.`;
          return;
        }
      }

      /* save data */
      this.data = this.data.splice(headerRowIndex + 1);
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < this.data.length; i++) {
        const d = this.data[i];
        if (d[0] === undefined) {
          break;
        }
        const p = {
          name: '',
          firstName: '',
          middleName: '',
          lastName: d[lastNameHeaderIndex],
          ssn:
            d[ssnHeaderIndex] === undefined ? '' : d[ssnHeaderIndex].toString(),
          caseNumber: this.validateCaseNumber ? d[caseHeaderIndex] : '',
          amount:
            d[amountHeaderIndex] === undefined ? 0 : +d[amountHeaderIndex],
          isSelected: true,
          isValidated: false,
          hasError: false,
          hasWarning: false,
          hadEdit: false,
          errorMessage: '',
        };
        this.payorCollection.push(p);
      }
    };
    reader.readAsBinaryString(target.files[0]);

    this.payorFrmGp.patchValue({
      filename: target.files[0].name,
    });
    this.fileImported = true;
    this.isFileValid = true;
    this.payorCollection.reverse();
    reader.onloadend = (e: any) => {
      this.isAllChecked();
      this.payorCollection.forEach((element) => {
        if (element.isSelected) {
          this.atLeastOnePayorSelected = true;
        }
      });
      if (this.payorCollection.length === 0 && this.isFileValid) {
        this.isFileValid = false;
        this.fileErrorMessage =
          'Error importing file.  Please review file to make sure all fields are completed.';
      }
    };
    target = new DataTransfer();
  }

  resetFile() {
    this.inputFile.nativeElement.value = null;
  }

  addSavePayorCollection() {
    this.payorsValid = false;
    this.numInvalid = 0;
    this.numWarning = 0;
    this.noPreviousPayors = '';
    const payor = this.payorFrmGp.value;
    if (
      payor.collectionFrmGp.lastName === '' ||
      payor.collectionFrmGp.lastName === null ||
      //Don't care if ssn is missing for TN
      // payor.collectionFrmGp.ssn === '' ||
      // payor.collectionFrmGp.ssn === null ||
      payor.collectionFrmGp.amount === '' ||
      payor.collectionFrmGp.amount === null ||
      ((payor.collectionFrmGp.casenumber === '' ||
        payor.collectionFrmGp.casenumber === null) &&
        this.validateCaseNumber)
    ) {
      if (!this.validateCaseNumber) {
        this.payorDetailError =
          'Invalid entry, make sure Last Name, SSN, and Amount have been entered.';
      } else {
        this.payorDetailError =
          'Invalid entry, make sure Last Name, SSN, ' +
          this.secondaryIdAlias +
          ' and Amount have been entered.';
      }
      return;
    } else {
      this.payorDetailError = '';
    }
    let founderror = false;
    let formatSSN = '';
    const ssnRegex = new RegExp(this.ssnPattern);

    if (payor.collectionFrmGp.ssn.length > 0) {
      formatSSN = payor.collectionFrmGp.ssn.toString();
      formatSSN = formatSSN.replace(/-/g, '');
      formatSSN = formatSSN.replace(/\s/g, '');

      if (!ssnRegex.test(payor.collectionFrmGp.ssn)) {
        founderror = true;
      }
    }

    if (!this.isEditPayor) {
      const p = {
        lastName: payor.collectionFrmGp.lastname,
        ssn: formatSSN,
        amount: +payor.collectionFrmGp.amount,
        caseNumber: payor.collectionFrmGp.casenumber,
        firstName: '',
        middleName: '',
        name: '',
        isSelected: true,
        isValidated: false,
        hasError: founderror,
        hasWarning: false,
        hadEdit: false,
        errorMessage: founderror ? 'Invalid SSN' : '',
      };
      this.payorCollection.push(p);
      this.payorCollection.reverse();
    } else {
      this.payorCollection[this.editPayorIndex].lastName =
        payor.collectionFrmGp.lastname;
      this.payorCollection[this.editPayorIndex].ssn = formatSSN;
      this.payorCollection[this.editPayorIndex].caseNumber =
        payor.collectionFrmGp.casenumber;
      this.payorCollection[this.editPayorIndex].amount =
        payor.collectionFrmGp.amount;
      this.payorCollection[this.editPayorIndex].hasError = false;
      this.payorCollection[this.editPayorIndex].errorMessage = '';
      this.payorCollection[this.editPayorIndex].isValidated = false;

      //If we have a SSN, then make sure it's valid
      if (payor.collectionFrmGp.ssn.length > 0) {
        if (!ssnRegex.test(payor.collectionFrmGp.ssn)) {
          this.payorCollection[this.editPayorIndex].hasError = true;
        } else {
          this.payorCollection[this.editPayorIndex].hasError = false;
        }
      }
      this.payorCollection[this.editPayorIndex].hadEdit = true;

      this.isEditPayor = false;
      this.editPayorIndex = -1;
    }

    this.payorsValid = false;
    this.getNumberOfPayments();
    this.payorFrmGp.reset();
    this.payorFrmGp.updateValueAndValidity();
  }

  resetToggle() {
    this.toggleEditPayor = false;
  }

  editPayor(payors: IEMPPaymentCollection, index: number) {
    this.toggleEditPayor = true;
    this.nameField.nativeElement.focus();
    this.isEditPayor = true;
    this.editPayorIndex = index;
    this.payorFrmGp.reset();

    this.payorFrmGp.get('collectionFrmGp')!.patchValue({
      lastname: payors.lastName,
      ssn: payors.ssn,
      amount: payors.amount,
      casenumber: payors.caseNumber,
    });
    this.payorFrmGp.updateValueAndValidity();
    this.numInvalid = 0;
    this.numWarning = 0;
    this.payorsValid = false;
    this.payorCollection.forEach((payor) => {
      payor.isValidated = false;
      payor.hadEdit = false;
    });
  }

  deletePayor(i: number) {
    this.payorCollection.splice(i, 1);
    this.numInvalid = 0;
    this.numWarning = 0;
    this.payorsValid = false;
    this.payorCollection.forEach((payor) => {
      payor.isValidated = false;
    });
    this.isAllChecked();
  }

  selectAllPayments(event: any) {
    if (event.target.checked) {
      this.payorCollection.forEach((p) => {
        p.isSelected = true;
      });
    } else {
      this.payorCollection.forEach((p) => {
        p.isSelected = false;
      });
    }
    this.getNumberOfPayments();
  }

  clearpayorCollection() {
    this.fileImported = false;
    this.isFileValid = false;
    this.data = null;
    //this.payment = {} as IEMPPayment;
    this.payorCollection = [];
    this.numOfPayments = 0;
    this.payorFrmGp.reset();
    this.payorFrmGp.patchValue({ filename: ' Choose File' });
  }

  isAllChecked() {
    let checkit = true;
    this.payorCollection.forEach((element) => {
      if (!element.isSelected) {
        checkit = false;
      }
    });
    this.allChecked = checkit;
  }

  addRemovePayor(event: any, i: number) {
    if (event.target.checked) {
      this.payorCollection[i].isSelected = true;
    } else {
      this.payorCollection[i].isSelected = false;
    }
    this.getNumberOfPayments();
    this.isAllChecked();
  }

  getNumberOfPayments(): number {
    this.numOfPayments = 0;
    this.totalAmtOfPayments = 0;
    this.atLeastOnePayorSelected = false;
    this.payorCollection.forEach((element) => {
      if (element.isSelected) {
        this.atLeastOnePayorSelected = true;
        this.numOfPayments++;
        this.totalAmtOfPayments += +element.amount;
      }
    });
    this.totalAmtOfPayments = +this.totalAmtOfPayments.toFixed(2);
    return this.numOfPayments;
  }

  setValidationType() {
    if (this.validationType === PayorValidationTypes.creditCard) {
      this.isCreditCardValidation = true;
      this.isEftFileValidation = false;
      this.isScheduledPaymentValidation = false;
    }
    if (this.validationType === PayorValidationTypes.scheduldedPayment) {
      this.isCreditCardValidation = false;
      this.isEftFileValidation = false;
      this.isScheduledPaymentValidation = true;
    }
    if (this.validationType === PayorValidationTypes.eftEdiFile) {
      this.isCreditCardValidation = false;
      this.isEftFileValidation = true;
      this.isScheduledPaymentValidation = false;
    }
  }

  logError(error: Error) {
    this.errorHandlerService.logError(error);
  }

  handleError(error: number) {
    this.errorHandlerService.handleErrorMessage(error, this.baseurl);
  }
}

